// Inventory Management Type Definitions

// Enums
export enum ProductCategory {
  HairProduct = 0,
  StylingProduct = 1,
  ColoringProduct = 2,
  Tool = 3,
  DisposableSupply = 4,
  SkinCareProduct = 5,
  Equipment = 6,
  Retail = 7,
  Cleaning = 8,
  Other = 9
}

export enum StockTransactionType {
  Purchase = 0,
  Usage = 1,
  Adjustment = 2,
  Waste = 3,
  Transfer = 4,
  Return = 5,
  Sale = 6,
  Donation = 7,
  Initial = 8
}

export enum StockAlertSeverity {
  Low = 0,
  Medium = 1,
  High = 2,
  Critical = 3
}

// Product Types
export interface ProductDto {
  id: number;
  name: string;
  sku: string;
  description?: string;
  category: ProductCategory;
  categoryName: string;
  unit: string;
  defaultCostPerUnit: number;
  lowStockThreshold: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductDto {
  name: string;
  sku: string;
  description?: string;
  category: ProductCategory;
  unit: string;
  defaultCostPerUnit: number;
  lowStockThreshold: number;
}

export interface UpdateProductDto {
  name?: string;
  sku?: string;
  description?: string;
  category?: ProductCategory;
  unit?: string;
  defaultCostPerUnit?: number;
  lowStockThreshold?: number;
  isActive?: boolean;
}

// Inventory Item Types
export interface InventoryItemDto {
  id: number;
  productId: number;
  productName: string;
  productSku: string;
  productCategory: ProductCategory;
  productCategoryName: string;
  barbershopId: number;
  barbershopName: string;
  currentQuantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  unit: string;
  costPerUnit: number;
  totalValue: number;
  lowStockThreshold: number;
  expirationDate?: string;
  location?: string;
  lastRestockDate?: string;
  lastUpdated: string;
  isLowStock: boolean;
  isCriticallyLow: boolean;
}

export interface CreateInventoryItemDto {
  productId: number;
  barbershopId: number;
  initialQuantity: number;
  costPerUnit: number;
  expirationDate?: string;
  location?: string;
}

export interface UpdateInventoryItemDto {
  costPerUnit?: number;
  lowStockThreshold?: number;
  expirationDate?: string;
  location?: string;
}

// Stock Transaction Types
export interface StockTransactionDto {
  id: number;
  inventoryItemId: number;
  productName: string;
  productSku: string;
  barbershopId: number;
  barbershopName: string;
  transactionType: StockTransactionType;
  transactionTypeName: string;
  quantityChanged: number;
  quantityBefore: number;
  quantityAfter: number;
  costPerUnit: number;
  totalCost: number;
  notes?: string;
  referenceNumber?: string;
  performedBy: string;
  performedByName: string;
  transactionDate: string;
  createdAt: string;
}

export interface RecordPurchaseDto {
  quantity: number;
  costPerUnit: number;
  notes?: string;
  referenceNumber?: string;
}

export interface RecordUsageDto {
  quantity: number;
  notes?: string;
}

export interface RecordAdjustmentDto {
  quantityChange: number;
  reason: string;
  notes?: string;
}

export interface RecordWasteDto {
  quantity: number;
  reason: string;
  notes?: string;
}

// Stock Alert Types
export interface StockAlertDto {
  id: number;
  inventoryItemId: number;
  productName: string;
  productSku: string;
  barbershopId: number;
  barbershopName: string;
  severity: StockAlertSeverity;
  severityName: string;
  message: string;
  currentQuantity: number;
  threshold: number;
  isAcknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedByName?: string;
  acknowledgedAt?: string;
  createdAt: string;
}

export interface AcknowledgeAlertDto {
  notes?: string;
}

// Inventory Report Types
export interface InventoryReportDto {
  barbershopId: number;
  barbershopName: string;
  reportDate: string;
  totalItems: number;
  totalValue: number;
  lowStockCount: number;
  criticalStockCount: number;
  expiringItemsCount: number;
  categoryBreakdown: CategoryStockDto[];
  topValueItems: InventoryItemDto[];
  expiringItems: InventoryItemDto[];
  recentTransactions: StockTransactionDto[];
}

export interface CategoryStockDto {
  category: ProductCategory;
  categoryName: string;
  itemCount: number;
  totalQuantity: number;
  totalValue: number;
  lowStockCount: number;
  percentage: number;
}

// Pagination Types
export interface InventoryListResponse {
  items: InventoryItemDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ProductListResponse {
  items: ProductDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TransactionListResponse {
  items: StockTransactionDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AlertListResponse {
  items: StockAlertDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Filter Types
export interface InventoryFilters {
  search?: string;
  category?: ProductCategory;
  barbershopId?: number;
  lowStockOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface TransactionFilters {
  // Backend StockHistoryQueryDto filters by ProductId (there is no inventory-item filter).
  productId?: number;
  barbershopId?: number;
  transactionType?: StockTransactionType;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

// Helper Types
export interface CategoryOption {
  value: ProductCategory;
  label: string;
  color: string;
}

export interface TransactionTypeOption {
  value: StockTransactionType;
  label: string;
  color: string;
}

export interface SeverityOption {
  value: StockAlertSeverity;
  label: string;
  color: string;
  bgColor: string;
}

// Service-Product Association Types
export interface ServiceProductUsageDto {
  id: number;
  serviceId: number;
  productId: number;
  productName: string;
  productSku: string;
  productCategory: string;
  estimatedQuantityPerService: number;
  isRequired: boolean;
  notes?: string;
  createdAt: string;
}

export interface AssignProductToServiceDto {
  productId: number;
  estimatedQuantityPerService?: number;
  isRequired?: boolean;
  notes?: string;
}
